import { useState, useEffect } from "react";
import { Modal, Select, Checkbox } from "antd";
import {
  EditOutlined,
  FundProjectionScreenOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import type {
  Monitor,
  MonitorColorBy,
  MonitorView,
} from "../../../../api/monitors";
import type { UserListItemOut } from "../../../../api/auth";
import type { Station } from "../../../../api/stations";
import {
  ColorByPicker,
  ScreenModePicker,
  ShpickerTag,
  ShpickerVisibilityPicker,
  SplitStationsPicker,
  StationPickGrid,
  UserOptionLabel,
  ViewPicker,
} from "./monitorFormOptions";
import {
  ConfigFormAntdCss,
  FormModalShell,
  FormStepField as Field,
} from "../panel/formParts";
import { useUnsavedChangesGuard } from "../panel/useUnsavedChangesGuard";

// Опциите на user Select-а: label остава чист низ заради търсачката,
// баджът „Шпикер" се рендира през optionRender.
const userSelectOptions = (users: UserListItemOut[]) =>
  users.map((user) => ({
    value: user.id,
    label: user.username,
    shpicker: user.shpicker,
  }));
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles } from "../../../../utils/modalStyles";

const R = {
  bg: "var(--color-canvas)",
  text: "var(--color-ink)",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  brandTint2: "var(--color-brand-tint2)",
};

// Стойностите на формата — какъвто е договорът на create/update endpoint-ите.
export type MonitorFormValues = {
  userId: number;
  stationIds: number[];
  aggregatedQuantities: boolean;
  completeFullQuantity: boolean;
  view: MonitorView;
  showAllStatuses: boolean;
  colorBy: MonitorColorBy;
  splitScreen: boolean;
  leftStationIds: number[];
  rightStationIds: number[];
};

interface Props {
  open: boolean;
  // Създаване (monitor=null) или редакция (monitor подаден за префил).
  monitor?: Monitor | null;
  // За какъв екран е формата — определя kicker текста при създаване.
  variant: "monitor" | "shpicker";
  // Избираемите потребители — извикващият ги е филтрирал (свободни/същия вид).
  users: UserListItemOut[];
  stations: Station[];
  // Извикващият прави мутацията и toast-овете; при грешка модалът остава отворен.
  onSubmit: (values: MonitorFormValues) => Promise<void>;
  onCancel: () => void;
}

/**
 * Общият модал за създаване И редакция на монитор/шпикер. Разположен е в
 * две колони на PC, за да се събере без скрол; на таблет/телефон пада в
 * една колона и скролва. Динамични части: „Видимост" само за шпикер,
 * лява/дясна решетка при разделен екран.
 */
const MonitorFormModal = ({
  open,
  monitor = null,
  variant,
  users,
  stations,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const isEdit = monitor !== null;

  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    monitor?.userId ?? null,
  );
  const [selectedStationIds, setSelectedStationIds] = useState<number[]>(
    monitor?.stationIds ?? [],
  );
  const [aggregatedQuantities, setAggregatedQuantities] = useState(
    monitor?.aggregatedQuantities ?? false,
  );
  const [completeFullQuantity, setCompleteFullQuantity] = useState(
    monitor?.completeFullQuantity ?? false,
  );
  const [view, setView] = useState<MonitorView>(monitor?.view ?? "grid");
  const [showAllStatuses, setShowAllStatuses] = useState(
    monitor?.showAllStatuses ?? false,
  );
  const [colorBy, setColorBy] = useState<MonitorColorBy>(
    monitor?.colorBy ?? "table",
  );
  // Разделен екран: лява/дясна половина със свои станции.
  const [splitScreen, setSplitScreen] = useState(monitor?.splitScreen ?? false);
  const [leftStationIds, setLeftStationIds] = useState<number[]>(
    monitor?.leftStationIds ?? [],
  );
  const [rightStationIds, setRightStationIds] = useState<number[]>(
    monitor?.rightStationIds ?? [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Пазач за незапазени промени — пита при затваряне, ако нещо е пипано.
  const sortedIds = (ids: number[]) => [...ids].sort((a, b) => a - b);
  const { requestClose, markPristine, unsavedDialog } = useUnsavedChangesGuard({
    open,
    snapshot: JSON.stringify({
      selectedUserId,
      selectedStationIds: sortedIds(selectedStationIds),
      aggregatedQuantities,
      completeFullQuantity,
      view,
      showAllStatuses,
      colorBy,
      splitScreen,
      leftStationIds: sortedIds(leftStationIds),
      rightStationIds: sortedIds(rightStationIds),
    }),
    isSubmitting,
    onClose: onCancel,
  });

  // Видимостта („всички статуси") има смисъл само за шпикер монитор.
  const selectedUser = users.find((user) => user.id === selectedUserId);
  const selectedUserIsShpicker = !!selectedUser?.shpicker;

  useEffect(() => {
    if (open) {
      setSelectedUserId(monitor?.userId ?? null);
      setSelectedStationIds(monitor?.stationIds ?? []);
      setAggregatedQuantities(monitor?.aggregatedQuantities ?? false);
      setCompleteFullQuantity(monitor?.completeFullQuantity ?? false);
      setView(monitor?.view ?? "grid");
      setShowAllStatuses(monitor?.showAllStatuses ?? false);
      setColorBy(monitor?.colorBy ?? "table");
      setSplitScreen(monitor?.splitScreen ?? false);
      setLeftStationIds(monitor?.leftStationIds ?? []);
      setRightStationIds(monitor?.rightStationIds ?? []);
      markPristine();
    }
  }, [open, monitor, markPristine]);

  const toggleIn =
    (setIds: React.Dispatch<React.SetStateAction<number[]>>) =>
    (stationId: number) =>
      setIds((prev) =>
        prev.includes(stationId)
          ? prev.filter((id) => id !== stationId)
          : [...prev, stationId],
      );
  const toggleStation = toggleIn(setSelectedStationIds);
  const toggleLeftStation = toggleIn(setLeftStationIds);
  const toggleRightStation = toggleIn(setRightStationIds);

  // При split екранните станции са лява ∪ дясна; иначе — единият списък.
  const effectiveStationIds = splitScreen
    ? Array.from(new Set([...leftStationIds, ...rightStationIds]))
    : selectedStationIds;

  const canSave =
    !!selectedUserId &&
    !isSubmitting &&
    (splitScreen
      ? leftStationIds.length > 0 && rightStationIds.length > 0
      : selectedStationIds.length > 0);

  const handleSave = async () => {
    if (!canSave || !selectedUserId) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        userId: selectedUserId,
        stationIds: effectiveStationIds,
        aggregatedQuantities,
        completeFullQuantity,
        view,
        showAllStatuses: selectedUserIsShpicker ? showAllStatuses : false,
        colorBy,
        splitScreen,
        leftStationIds: splitScreen ? leftStationIds : [],
        rightStationIds: splitScreen ? rightStationIds : [],
      });
    } catch {
      // Toast-ът за грешка е при извикващия; модалът остава отворен.
    } finally {
      setIsSubmitting(false);
    }
  };

  // Широк модал: на PC формата е в две колони и не скролва (скролът остава
  // само предпазен fallback); таблет/телефон скролват нормално.
  const modalWidth = isMobile ? undefined : isTablet ? 640 : 1180;
  const kicker = isEdit
    ? t("monitorsConfig.editTitle")
    : variant === "shpicker"
      ? t("monitorsConfig.configKickerShpicker")
      : t("monitorsConfig.configKicker");

  // Номерата на стъпките са динамични: „Видимост" се вмъква само при шпикер
  // и измества „Оцветяване" и „Опции" с едно напред.
  const stepLabel = (index: number, key: string) => `${index}. ${t(key)}`;
  const colorByStepIndex = selectedUserIsShpicker ? 6 : 5;
  const optionsStepIndex = selectedUserIsShpicker ? 7 : 6;

  // Двете опции-чекбокса; посоката зависи от мястото им в решетката.
  const optionsCheckboxes = (direction: "row" | "column") => (
    <div
      style={{
        display: "flex",
        flexDirection: direction,
        flexWrap: direction === "row" ? "wrap" : undefined,
        // ВНИМАНИЕ: rowGap: undefined би изтрил редовия отстъп от gap
        // shorthand-а (React маха свойството) — затова е винаги изричен.
        gap: direction === "row" ? 48 : 12,
        rowGap: 8,
        paddingTop: 12,
      }}
    >
      <Checkbox
        checked={aggregatedQuantities}
        onChange={(e) => setAggregatedQuantities(e.target.checked)}
      >
        <span style={{ fontSize: 13, color: R.text }}>
          {t("monitorsConfig.aggregatedQuantities")}
        </span>
      </Checkbox>
      <Checkbox
        checked={completeFullQuantity}
        onChange={(e) => setCompleteFullQuantity(e.target.checked)}
      >
        <span style={{ fontSize: 13, color: R.text }}>
          {t("monitorsConfig.completeFullQuantity")}
        </span>
      </Checkbox>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={requestClose}
        // Esc го поема пазачът за незапазени промени (useUnsavedChangesGuard).
        keyboard={false}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={modalWidth}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <ConfigFormAntdCss />
        <FormModalShell
          icon={
            isEdit ? (
              <EditOutlined style={{ fontSize: 18 }} />
            ) : variant === "shpicker" ? (
              <ThunderboltFilled style={{ fontSize: 18 }} />
            ) : (
              <FundProjectionScreenOutlined style={{ fontSize: 18 }} />
            )
          }
          kicker={kicker}
          title={t("monitorsConfig.configTitle")}
          titleExtra={
            // Баджът „Шпикер" стои до заглавието; целият хинт е в tooltip-а му.
            selectedUserIsShpicker ? (
              <span title={t("monitorsConfig.shpickerHint")}>
                <ShpickerTag />
              </span>
            ) : undefined
          }
          isNarrow={isNarrow}
          saveLabel={
            isEdit
              ? t("monitorsConfig.saveChanges")
              : t("monitorsConfig.saveConfig")
          }
          canSave={canSave}
          isSubmitting={isSubmitting}
          onSave={handleSave}
          onCancel={requestClose}
        >
          {/* Body: редове с въздух; на PC всеки ред е своя решетка, на
            телефон/таблет всичко пада в една колона и скролва */}
          <div
            className="cfg-form"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: isNarrow ? "12px 14px" : "16px 20px",
              background: R.bg,
              display: "flex",
              flexDirection: "column",
              gap: isNarrow ? 12 : 14,
            }}
          >
            {/* Ред 1: късо поле „Потребител" + широки карти „Екран" */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isNarrow ? "1fr" : "360px 1fr",
                gap: isNarrow ? 12 : 14,
                alignItems: "stretch",
              }}
            >
              <Field label={stepLabel(1, "monitorsConfig.stepUser")}>
                <Select
                  showSearch
                  allowClear={!isEdit}
                  placeholder={t("monitorsConfig.selectMonitorPlaceholder")}
                  value={selectedUserId}
                  onChange={(value) => setSelectedUserId(value ?? null)}
                  style={{ width: "100%" }}
                  size="middle"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={userSelectOptions(users)}
                  optionRender={(option) => (
                    <UserOptionLabel
                      username={String(option.data.label)}
                      shpicker={Boolean(option.data.shpicker)}
                    />
                  )}
                />
              </Field>
              <Field label={stepLabel(2, "monitorsConfig.stepScreen")}>
                <ScreenModePicker
                  value={splitScreen}
                  onChange={setSplitScreen}
                  isNarrow={isNarrow}
                />
              </Field>
            </div>

            {/* Ред 2: Станции — една решетка или лява/дясна при split */}
            <Field
              label={stepLabel(3, "monitorsConfig.stepStations")}
              right={
                !splitScreen && selectedStationIds.length > 0 ? (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: R.brandDeep,
                      background: R.brandTint,
                      padding: "3px 10px",
                      borderRadius: 999,
                      border: `1px solid ${R.brandTint2}`,
                      letterSpacing: 0.3,
                    }}
                  >
                    {t("monitorsConfig.selected", {
                      count: selectedStationIds.length,
                    })}
                  </span>
                ) : null
              }
            >
              {splitScreen ? (
                <SplitStationsPicker
                  stations={stations}
                  leftIds={leftStationIds}
                  rightIds={rightStationIds}
                  onToggleLeft={toggleLeftStation}
                  onToggleRight={toggleRightStation}
                  disabled={!selectedUserId}
                  isNarrow={isNarrow}
                />
              ) : (
                <StationPickGrid
                  stations={stations}
                  selectedIds={selectedStationIds}
                  onToggle={toggleStation}
                  disabled={!selectedUserId}
                  isNarrow={isNarrow}
                />
              )}
            </Field>

            {/* Ред 3: Изглед + (Видимост при шпикер / Оцветяване иначе) */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isNarrow
                  ? "1fr"
                  : selectedUserIsShpicker
                    ? "1fr 1fr"
                    : "2fr 3fr",
                gap: isNarrow ? 12 : 14,
                alignItems: "stretch",
              }}
            >
              <Field label={stepLabel(4, "monitorsConfig.stepView")}>
                <ViewPicker
                  value={view}
                  onChange={setView}
                  isNarrow={isNarrow}
                />
              </Field>
              {selectedUserIsShpicker ? (
                <Field label={stepLabel(5, "monitorsConfig.stepVisibility")}>
                  <ShpickerVisibilityPicker
                    value={showAllStatuses}
                    onChange={setShowAllStatuses}
                    isNarrow={isNarrow}
                  />
                </Field>
              ) : (
                <Field
                  label={stepLabel(
                    colorByStepIndex,
                    "monitorsConfig.stepColorBy",
                  )}
                >
                  <ColorByPicker
                    value={colorBy}
                    onChange={setColorBy}
                    isNarrow={isNarrow}
                  />
                </Field>
              )}
            </div>

            {/* Ред 4: при шпикер Оцветяване + Опции; иначе само Опции */}
            {selectedUserIsShpicker ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow ? "1fr" : "3fr 2fr",
                  gap: isNarrow ? 12 : 14,
                  alignItems: "stretch",
                }}
              >
                <Field
                  label={stepLabel(
                    colorByStepIndex,
                    "monitorsConfig.stepColorBy",
                  )}
                >
                  <ColorByPicker
                    value={colorBy}
                    onChange={setColorBy}
                    isNarrow={isNarrow}
                  />
                </Field>
                <Field
                  label={stepLabel(
                    optionsStepIndex,
                    "monitorsConfig.stepOptions",
                  )}
                >
                  {optionsCheckboxes("column")}
                </Field>
              </div>
            ) : (
              <Field
                label={stepLabel(
                  optionsStepIndex,
                  "monitorsConfig.stepOptions",
                )}
              >
                {optionsCheckboxes("row")}
              </Field>
            )}
          </div>
        </FormModalShell>
      </Modal>
      {unsavedDialog}
    </>
  );
};

export default MonitorFormModal;
